import { useEffect, useId, useRef, useState } from "react";
import { formatExactQuantity } from "../../utils/inventoryUnitQuantity";
import "./InventoryCountStockImpact.css";

const messages = {
  en: {
    title: "Stock impact by item", search: "Find an item by name or code", close: "Close stock impact", all: "All items",
    description: "Compare the saved stock with the result of closing this count, including later movements.",
    location: "Location", inventory: "Count date", current: "Current date", before: "Before closing", after: "After closing",
    unit: "Unit", hidden: "Restricted", pending: "Pending decision", noItems: "No items match your search.",
    stale: "The review has changed. Update the review before consulting the stock impact.",
    needsReview: "A decision or correction is still needed. Unresolved results are shown as pending.",
    unavailable: "The stock impact by location is unavailable for this item.",
    scopeUnavailable: "This count does not identify locations. Its impact cannot be broken down by location.",
    locationsUnavailable: "No locations are available for this analysis under your current access.",
    blindUnavailable: "Stock impact is hidden for blind counts.",
    scrollHint: "Swipe to see the columns for both dates.",
    laterCount: "A later count fixes this location's current stock; its balance is preserved.",
    notCounted: "This location is outside the entered count; later movements remain included.",
    rounding: "Automatic adjustment below 0.01",
    roundingExplanation: "Allocated to this location's existing stock. Total stock and valuation remain unchanged.",
    negative: "The proposed count would leave negative current stock. Review the count or its date before finalizing.",
    readonly: "Consulting this view does not change stock or finalize the count.",
  },
  es: {
    title: "Impacto en el stock por artículo", search: "Buscar un artículo por nombre o código", close: "Cerrar impacto en el stock", all: "Todos los artículos",
    description: "Compara el stock registrado con el resultado de cerrar este recuento, incluidos los movimientos posteriores.",
    location: "Ubicación", inventory: "Fecha del recuento", current: "Fecha actual", before: "Antes de cerrar", after: "Después de cerrar",
    unit: "Unidad", hidden: "Restringido", pending: "Decisión pendiente", noItems: "No hay artículos que coincidan con la búsqueda.",
    stale: "La revisión ha cambiado. Actualiza la revisión antes de consultar el impacto en el stock.",
    needsReview: "Todavía falta una decisión o corrección. Los resultados sin resolver se muestran como pendientes.",
    unavailable: "El impacto del stock por ubicación no está disponible para este artículo.",
    scopeUnavailable: "Este recuento no identifica ubicaciones. No se puede desglosar su impacto por ubicación.",
    locationsUnavailable: "Tus permisos actuales no permiten consultar ubicaciones para este análisis.",
    blindUnavailable: "El impacto en el stock está oculto en los recuentos ciegos.",
    scrollHint: "Desliza para ver las columnas de ambas fechas.",
    laterCount: "Un recuento posterior fija el stock actual de esta ubicación; se conserva su saldo.",
    notCounted: "Esta ubicación queda fuera del recuento introducido; se incluyen sus movimientos posteriores.",
    rounding: "Regularización automática inferior a 0,01",
    roundingExplanation: "Se aplica al stock existente de esta ubicación. El stock total y su valoración no cambian.",
    negative: "El recuento propuesto dejaría stock actual negativo. Revisa las cantidades o la fecha antes de finalizar.",
    readonly: "Consultar esta vista no modifica el stock ni finaliza el recuento.",
  },
};

const noteText = (code, words) => ({ LATER_COUNT_PRESERVES_CURRENT: words.laterCount, NOT_COUNTED: words.notCounted,
  AUTOMATIC_ROUNDING_CORRECTION: words.rounding,
  NEGATIVE_CURRENT_STOCK: words.negative, INVENTORY_COUNT_HISTORICAL_LOCATION_NEGATIVE: words.negative,
  INVENTORY_COUNT_IMPACT_LOCATION_SCOPE_UNAVAILABLE: words.scopeUnavailable,
  INVENTORY_COUNT_IMPACT_LOCATIONS_UNAVAILABLE: words.locationsUnavailable,
  INVENTORY_COUNT_IMPACT_BLIND: words.blindUnavailable }[code]);

export default function InventoryCountStockImpact({ rows = [], initialItemId = null, language = "en", blind = false, stale = false, onClose }) {
  const words = messages[language] || messages.en;
  const titleId = useId();
  const panelRef = useRef(null);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  const [itemId, setItemId] = useState(initialItemId);
  const [search, setSearch] = useState("");
  const row = rows.find((entry) => Number(entry.item_id) === Number(itemId));
  const impact = row?.stock_impact;
  const query = search.trim().toLocaleLowerCase();
  const visibleItems = rows.filter((entry) => `${entry.item_name || ""} ${entry.item_code || ""}`.toLocaleLowerCase().includes(query));
  const quantity = (value, exact, after = false) => blind ? words.hidden : value == null
    ? after && impact?.status === "needs_review" ? words.pending : words.hidden
    : formatExactQuantity(exact, "unit", language) || new Intl.NumberFormat(language === "es" ? "es-ES" : "en-GB", { maximumFractionDigits: 8 }).format(value);

  useEffect(() => {
    const previous = document.activeElement;
    const panel = panelRef.current;
    panel?.focus({ preventScroll: true });
    // The count workspace traps Escape/Tab at document capture. Window capture
    // owns those keys while this nested dialog is open, without closing its parent.
    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        event.preventDefault(); event.stopImmediatePropagation(); closeRef.current();
      } else if (event.key === "Tab") {
        event.stopImmediatePropagation();
        const controls = [...panel.querySelectorAll("button:not(:disabled), input:not(:disabled), [tabindex='0']")];
        const first = controls[0]; const last = controls[controls.length - 1];
        if (!first) { event.preventDefault(); panel.focus(); }
        else if (event.shiftKey && (document.activeElement === first || document.activeElement === panel)) { event.preventDefault(); last.focus(); }
        else if (!event.shiftKey && (document.activeElement === last || !panel.contains(document.activeElement))) { event.preventDefault(); first.focus(); }
      }
    };
    window.addEventListener("keydown", onKeyDown, true);
    return () => {
      window.removeEventListener("keydown", onKeyDown, true);
      if (previous?.isConnected) previous.focus({ preventScroll: true });
    };
  }, []);

  const selectItem = (id) => {
    setItemId(id);
    panelRef.current?.focus({ preventScroll: true });
  };
  return <div className="ic-dialogOverlay ic-stock-impact-overlay">
    <div ref={panelRef} className="ic-dialog ic-stock-impact" data-inventory-count-impact role="dialog" aria-modal="true" aria-labelledby={titleId} tabIndex={-1}>
      <header>
        <h3 id={titleId} className="ic-dialogTitle">{row ? row.item_name : words.title}</h3>
        {row && <p className="ic-stock-impact-identity">{row.item_code}{(impact?.unit_name || row.unit_name) && ` · ${words.unit}: ${impact?.unit_name || row.unit_name}`}</p>}
        <p>{words.description}</p>
      </header>
      <div className="ic-stock-impact-body">
        {stale ? <p role="alert" className="ic-final-review-notice">{words.stale}</p> : !row ? <>
          <label className="ic-stock-impact-search"><span>{words.search}</span><input type="search" value={search} onChange={(event) => setSearch(event.target.value)} /></label>
          <div className="ic-stock-impact-items">
            {visibleItems.map((item) => <button type="button" className="ic-stock-impact-item" key={item.item_id} onClick={() => selectItem(item.item_id)}>
              <strong>{item.item_name}</strong><span>{item.item_code || "—"}</span>
            </button>)}
            {!visibleItems.length && <p role="status">{words.noItems}</p>}
          </div>
        </> : !impact || impact.status === "unavailable" ? <div role="status"><p>{words.unavailable}</p>
          {(impact?.issues || []).map((issue, index) => <p key={index}>{noteText(issue.code, words) || issue.message}</p>)}
        </div> : <>
          {impact.status === "needs_review" && <p role="status" className="ic-final-review-notice">{words.needsReview}</p>}
          {!blind && impact.automatic_rounding && <p role="status" className="ic-final-review-notice">
            <strong>{words.rounding}: </strong>{impact.automatic_rounding.quantity > 0 ? "+" : ""}{quantity(impact.automatic_rounding.quantity)} {impact.unit_name} · {impact.automatic_rounding.location_name}. {words.roundingExplanation}
          </p>}
          {(impact.issues || []).map((issue, index) => <p className="ic-final-review-error" role="alert" key={index}>{noteText(issue.code, words) || issue.message || words.needsReview}</p>)}
          <div className="ic-stock-impact-table-wrap"><table className="ic-stock-impact-table">
            <thead><tr><th rowSpan={2} scope="col">{words.location}</th>
              <th colSpan={2} scope="colgroup">{words.inventory}<strong>{impact.inventory_date}</strong></th>
              <th colSpan={2} scope="colgroup">{words.current}<strong>{impact.current_date}</strong></th></tr>
              <tr><th scope="col">{words.before}</th><th scope="col">{words.after}</th><th scope="col">{words.before}</th><th scope="col">{words.after}</th></tr></thead>
            <tbody>{(impact.locations || []).map((location) => <tr key={location.location_id}>
              <th scope="row">{location.location_name}{noteText(location.note_code, words) && <small>{noteText(location.note_code, words)}</small>}{noteText(location.count_effect_note_code, words) && <small>{noteText(location.count_effect_note_code, words)}</small>}</th>
              <td>{quantity(location.inventory_before, location.inventory_before_exact)}</td><td className={!blind && location.inventory_after != null && location.inventory_after !== location.inventory_before ? "ic-stock-impact-changed" : undefined}>{quantity(location.inventory_after, location.inventory_after_exact, true)}</td>
              <td>{quantity(location.current_before, location.current_before_exact)}</td><td className={!blind && location.current_after != null && location.current_after !== location.current_before ? "ic-stock-impact-changed" : undefined}>{quantity(location.current_after, location.current_after_exact, true)}</td>
            </tr>)}</tbody>
          </table></div>
          <p className="ic-stock-impact-scroll-hint">{words.scrollHint}</p>
        </>}
      </div>
      <footer className="ic-dialogActions"><p>{words.readonly}</p>
        {row && !stale && <button type="button" className="ic-btn ic-btn--ghost" onClick={() => selectItem(null)}>{words.all}</button>}
        <button type="button" className="ic-btn ic-btn--primary" onClick={onClose}>{words.close}</button>
      </footer>
    </div>
  </div>;
}
